import React, { useState } from 'react';

import {
  ClonidineRecommendation,
  ClonidineRecommendationResult,
  getClonidineRecommendation,
  InuteroOpiate,
} from 'algorithms/clonidine-algorithm';
import {
  MorphineRecommendation,
  MorphineRecommendationResult,
  getDosageAndClonidineRecommendation,
} from 'algorithms/morphine-algorithm';
import { getComfortMeasures } from 'algorithms/comfort-measures-algorithm';
import { determineEvaluationScore } from 'algorithms/determine-evaluation-score-algorithm';
import { Symptoms } from 'types/assessment';
import { SymptomScore } from 'algorithms/types/symptom-score';

// Display maps for recommendation names
const MORPHINE_RECOMMENDATION_DISPLAY_MAP: Record<MorphineRecommendation, string> = {
  [MorphineRecommendation.Notstarted]: 'No Medication',
  [MorphineRecommendation.Start]: 'Start',
  [MorphineRecommendation.Increase]: 'Increase',
  [MorphineRecommendation.Nochange]: 'No Change',
  [MorphineRecommendation.Decrease]: 'Decrease',
  [MorphineRecommendation.Discontinue]: 'Discontinue',
  [MorphineRecommendation.Discharge]: 'Discharge',
};

const CLONIDINE_RECOMMENDATION_DISPLAY_MAP: Record<ClonidineRecommendation, string> = {
  [ClonidineRecommendation.StartQ3]: 'Start Q3',
  [ClonidineRecommendation.StartQ6]: 'Start Q6',
  [ClonidineRecommendation.Increase]: 'Increase',
  [ClonidineRecommendation.Decrease]: 'Decrease',
  [ClonidineRecommendation.Discontinue]: 'Discontinue',
  [ClonidineRecommendation.Discharge]: 'Consider Discharge',
  [ClonidineRecommendation.NoChange]: 'No Change',
  [ClonidineRecommendation.NoStarted]: 'No Medication',
};

// Helper functions to get display names
const getMorphineRecommendationDisplayName = (recommendation: MorphineRecommendation): string => {
  return MORPHINE_RECOMMENDATION_DISPLAY_MAP[recommendation] || MorphineRecommendation[recommendation];
};

const getClonidineRecommendationDisplayName = (recommendation: ClonidineRecommendation): string => {
  return CLONIDINE_RECOMMENDATION_DISPLAY_MAP[recommendation] || ClonidineRecommendation[recommendation];
};

type AlgorithmResults = {
  priorAssessmentScore: number;
  currentAssessmentScore: number;
  morphineRecommendation: MorphineRecommendationResult;
  clonidineRecommendation: ClonidineRecommendationResult;
  comfortMeasures: any;
};

type AlgorithmInputs = {
  priorAssessmentActiveSymptoms: Symptoms[];
  priorMorphineRecommendation: MorphineRecommendation;
  currentMorphineDose: number;
  currentAssessmentActiveSymptoms: Symptoms[];
  clonidineDose: number;
  clonidineFrequency: number;
  hasEverTakenClonidine: boolean;
  hasEverTakenMorphine: boolean;
  inuteroOpiates: InuteroOpiate;
  hoursSinceBirth: number;
  hoursSinceLastClonidineChange: number;
  hoursSinceLastMorphineChange: number;
  isIncreaseComparedToSixHoursAgo: boolean;
  lastAssessmentClonidineRecommendation: ClonidineRecommendation;
  hasTrialedOffClonidine: boolean;
};

export const AlgorithmTester: React.FC = () => {
  // State for inputs
  const [inputs, setInputs] = useState<AlgorithmInputs>({
    priorAssessmentActiveSymptoms: [],
    priorMorphineRecommendation: MorphineRecommendation.Notstarted,
    currentMorphineDose: 0,
    currentAssessmentActiveSymptoms: [],
    clonidineDose: 0,
    clonidineFrequency: 0,
    hasEverTakenClonidine: false,
    hasEverTakenMorphine: false,
    inuteroOpiates: {
      isTakingBuprenorphine: false,
      isTakingMethadone: false,
      isTakingOther: false,
    },
    hoursSinceBirth: 5,
    hoursSinceLastClonidineChange: 0,
    hoursSinceLastMorphineChange: 0,
    isIncreaseComparedToSixHoursAgo: false,
    lastAssessmentClonidineRecommendation: ClonidineRecommendation.NoStarted,
    hasTrialedOffClonidine: false,
  });

  // State for results
  const [results, setResults] = useState<AlgorithmResults | null>(null);

  const runAlgorithm = (algorithmInputs: AlgorithmInputs): AlgorithmResults => {
    const priorAssessmentScore = determineEvaluationScore(algorithmInputs.priorAssessmentActiveSymptoms);
    const currentAssessmentScore = determineEvaluationScore(algorithmInputs.currentAssessmentActiveSymptoms);

    const priorGISymptoms = {
      poorStooling: algorithmInputs.priorAssessmentActiveSymptoms.includes(Symptoms.Stools),
      poorFeeding: algorithmInputs.priorAssessmentActiveSymptoms.includes(Symptoms.Feeding),
    };
    const currentGISymptoms = {
      poorStooling: algorithmInputs.currentAssessmentActiveSymptoms.includes(Symptoms.Stools),
      poorFeeding: algorithmInputs.currentAssessmentActiveSymptoms.includes(Symptoms.Feeding),
    };

    const morphineRecommendation = getDosageAndClonidineRecommendation({
      priorEvaluationScore: priorAssessmentScore,
      currentEvaluationScore: currentAssessmentScore,
      priorMorphineRecommendation: algorithmInputs.priorMorphineRecommendation,
      priorAssessmentGISymptoms: priorGISymptoms,
      currentAssessmentGISymptoms: currentGISymptoms,
      morphineDose: algorithmInputs.currentMorphineDose,
    });
    const clonidineRecommendation = getClonidineRecommendation({
      clonidineDose: algorithmInputs.clonidineDose,
      clonidineFrequency: algorithmInputs.clonidineFrequency,
      morphineRecommendationResult: morphineRecommendation,
      morphineDose: algorithmInputs.currentMorphineDose,
      priorAssessmentGISymptoms: priorGISymptoms,
      currentAssessmentGISymptoms: currentGISymptoms,
      hasEverTakenClonidine: algorithmInputs.hasEverTakenClonidine,
      hasEverTakenMorphine: algorithmInputs.hasEverTakenMorphine,
      inuteroOpiates: algorithmInputs.inuteroOpiates,
      hoursSinceBirth: algorithmInputs.hoursSinceBirth,
      hoursSinceLastClonidineChange: algorithmInputs.hoursSinceLastClonidineChange,
      hoursSinceLastMorphineChange: algorithmInputs.hoursSinceLastMorphineChange,
      isIncreaseComparedToSixHoursAgo: algorithmInputs.isIncreaseComparedToSixHoursAgo,
      isLastAssessmentRecommendationAnIncrease:
        algorithmInputs.lastAssessmentClonidineRecommendation === ClonidineRecommendation.Increase,
      hasTrialedOffClonidine: algorithmInputs.hasTrialedOffClonidine,
    });

    if (clonidineRecommendation.recommendationCoded === ClonidineRecommendation.Discharge) {
      morphineRecommendation.recommendationCoded = MorphineRecommendation.Discharge;
      morphineRecommendation.recommendation = 'Consider Discharge';
      morphineRecommendation.note += '; Discharge based on Clonidine recommendation';
    }

    // Create symptom scores from active symptoms
    const symptomScores: SymptomScore[] = algorithmInputs.currentAssessmentActiveSymptoms.map(symptom => ({
      symptom_id: symptom,
      score: 1,
    }));

    const comfortRecommendations = [
      { symptom_id: Symptoms.Sleep, name: 'Skin-to-Skin' },
      { symptom_id: Symptoms.Sleep, name: 'Holding/Vertical Rocking' },
      { symptom_id: Symptoms.Sleep, name: 'Music' },
      { symptom_id: Symptoms.Sleep, name: 'Pacifier' },
      { symptom_id: Symptoms.Crying, name: 'Skin-to-Skin' },
      { symptom_id: Symptoms.Crying, name: 'Holding/Vertical Rocking' },
      { symptom_id: Symptoms.Crying, name: 'Music' },
      { symptom_id: Symptoms.Crying, name: 'Pacifier' },
      { symptom_id: Symptoms.Crying, name: 'Motion Devices (rockers, swings etc.)' },
      { symptom_id: Symptoms.Feeding, name: 'Skin-to-Skin' },
      { symptom_id: Symptoms.Feeding, name: 'Optimize Nutrition' },
      { symptom_id: Symptoms.Feeding, name: 'Music' },
      { symptom_id: Symptoms.Feeding, name: 'Pacifier' },
      { symptom_id: Symptoms.Stools, name: 'Optimize Nutrition' },
    ];

    const comfortMeasures = getComfortMeasures(symptomScores, comfortRecommendations);

    return {
      priorAssessmentScore,
      currentAssessmentScore,
      morphineRecommendation,
      clonidineRecommendation,
      comfortMeasures,
    };
  };

  const handleRunAlgorithm = () => {
    const algorithmResults = runAlgorithm(inputs);
    setResults(algorithmResults);
  };

  const updateInput = (key: keyof AlgorithmInputs, value: any) => {
    setInputs(prev => {
      const newInputs = { ...prev, [key]: value };

      // Auto-update hasEverTakenClonidine based on clonidine dose
      if (key === 'clonidineDose') {
        if (value > 0) {
          newInputs.hasEverTakenClonidine = true;
        } else {
          newInputs.hasEverTakenClonidine = false;
        }
      }

      // Auto-update hasEverTakenMorphine based on morphine dose
      if (key === 'currentMorphineDose') {
        if (value > 0) {
          newInputs.hasEverTakenMorphine = true;
        } else {
          newInputs.hasEverTakenMorphine = false;
        }
      }

      // Auto-update hasTrialedOffClonidine based on hasEverTakenClonidine
      if (key === 'hasEverTakenClonidine') {
        if (!value) {
          newInputs.hasTrialedOffClonidine = false;
          newInputs.isIncreaseComparedToSixHoursAgo = false;
          newInputs.hoursSinceLastClonidineChange = 0;
        }
      }

      // Auto-update hoursSinceLastMorphineChange based on hasEverTakenMorphine
      if (key === 'hasEverTakenMorphine') {
        if (!value) {
          newInputs.hoursSinceLastMorphineChange = 0;
        }
      }

      if (newInputs.clonidineDose === 0) {
        newInputs.clonidineFrequency = 0;
      }

      return newInputs;
    });
  };

  const updateInuteroOpiates = (key: keyof InuteroOpiate, value: boolean) => {
    setInputs(prev => ({
      ...prev,
      inuteroOpiates: { ...prev.inuteroOpiates, [key]: value },
    }));
  };

  const toggleSymptom = (symptom: Symptoms, isPrior: boolean) => {
    const key = isPrior ? 'priorAssessmentActiveSymptoms' : 'currentAssessmentActiveSymptoms';
    const currentSymptoms = inputs[key];
    const updatedSymptoms = currentSymptoms.includes(symptom)
      ? currentSymptoms.filter(s => s !== symptom)
      : [...currentSymptoms, symptom];
    updateInput(key, updatedSymptoms);
  };

  const getSymptomName = (symptomValue: string): string => {
    const symptomEntry = Object.entries(Symptoms).find(([key, value]) => value === symptomValue);
    return symptomEntry ? symptomEntry[0] : symptomValue;
  };

  const renderSymptomCheckboxes = (symptoms: Symptoms[], isPrior: boolean) => {
    // Filter active symptoms to just: Feeding, Stools, Crying, Sleep, temperature, tremors
    return Object.values(Symptoms)
      .filter(symptom =>
        [
          Symptoms.Feeding,
          Symptoms.Stools,
          Symptoms.Crying,
          Symptoms.Sleep,
          Symptoms.Temperature,
          Symptoms.Tremors,
        ].includes(symptom),
      )
      .map(symptom => (
        <label key={symptom} className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={symptoms.includes(symptom)}
            onChange={() => toggleSymptom(symptom, isPrior)}
            className="rounded"
          />
          <span className="text-sm">{getSymptomName(symptom)}</span>
        </label>
      ));
  };

  return (
    <div className="flex h-screen dark:bg-white-50 bg-white-50" style={{ backgroundColor: '#EEE' }}>
      {/* Left Side - Input Form */}
      <div className="w-1/2 p-6 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Algorithm Tester - Inputs</h2>

          {/* Prior Assessment Active Symptoms */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Prior Assessment Active Symptoms</h3>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-3">
              {renderSymptomCheckboxes(inputs.priorAssessmentActiveSymptoms, true)}
            </div>
          </div>

          {/* Prior Morphine Recommendation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Prior Morphine Recommendation</h3>
            <select
              value={inputs.priorMorphineRecommendation}
              onChange={e => updateInput('priorMorphineRecommendation', Number(e.target.value))}
              className="w-full p-2 border rounded"
            >
              {Object.entries(MorphineRecommendation)
                .filter(([key, value]) => isNaN(Number(key))) // Filter out numeric keys
                .map(([key, value]) => (
                  <option key={value} value={value}>
                    {getMorphineRecommendationDisplayName(value as MorphineRecommendation)}
                  </option>
                ))}
            </select>
          </div>

          {/* Current Morphine Dose */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Morphine Dose (mg/kg)</h3>
            <input
              type="number"
              step="0.001"
              value={inputs.currentMorphineDose}
              onChange={e => updateInput('currentMorphineDose', parseFloat(e.target.value) || 0)}
              className="w-full p-2 border rounded"
            />
          </div>

          {/* Current Assessment Active Symptoms */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Assessment Active Symptoms</h3>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-3">
              {renderSymptomCheckboxes(inputs.currentAssessmentActiveSymptoms, false)}
            </div>
          </div>

          {/* Last Assessment Clonidine Recommendation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Last Assessment Clonidine Recommendation</h3>
            <select
              value={inputs.lastAssessmentClonidineRecommendation}
              onChange={e => updateInput('lastAssessmentClonidineRecommendation', Number(e.target.value))}
              className="w-full p-2 border rounded"
            >
              {Object.entries(ClonidineRecommendation)
                .filter(([key, value]) => isNaN(Number(key))) // Filter out numeric keys
                .map(([key, value]) => (
                  <option key={value} value={value}>
                    {getClonidineRecommendationDisplayName(value as ClonidineRecommendation)}
                  </option>
                ))}
            </select>
          </div>

          {/* Clonidine Dose and Frequency */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Clonidine</h3>
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Dose (mcg/kg)</label>
                <input
                  type="number"
                  step="0.001"
                  value={inputs.clonidineDose}
                  onChange={e => updateInput('clonidineDose', parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="flex-1">
                <label
                  className={`block text-sm font-medium mb-1 ${inputs.clonidineDose === 0 ? 'text-gray-400' : ''}`}
                >
                  Frequency
                </label>
                <select
                  value={inputs.clonidineFrequency}
                  onChange={e => updateInput('clonidineFrequency', parseInt(e.target.value))}
                  disabled={inputs.clonidineDose === 0}
                  className={`w-full p-2 border rounded ${
                    inputs.clonidineDose === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                  }`}
                >
                  <option value={0}>0</option>
                  <option value={3}>3</option>
                  <option value={6}>6</option>
                </select>
              </div>
            </div>
          </div>

          {/* Boolean inputs */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Patient History</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.hasEverTakenMorphine}
                  onChange={e => updateInput('hasEverTakenMorphine', e.target.checked)}
                  disabled={inputs.currentMorphineDose > 0}
                  className="rounded"
                />
                <span className={inputs.currentMorphineDose > 0 ? 'text-gray-400' : ''}>Has Ever Taken Morphine</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.hasEverTakenClonidine}
                  onChange={e => updateInput('hasEverTakenClonidine', e.target.checked)}
                  disabled={inputs.clonidineDose > 0}
                  className="rounded"
                />
                <span className={inputs.clonidineDose > 0 ? 'text-gray-400' : ''}>Has Ever Taken Clonidine</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.isIncreaseComparedToSixHoursAgo}
                  onChange={e => updateInput('isIncreaseComparedToSixHoursAgo', e.target.checked)}
                  disabled={!inputs.hasEverTakenClonidine}
                  className="rounded"
                />
                <span className={!inputs.hasEverTakenClonidine ? 'text-gray-400' : ''}>
                  Clonidine Increased Witihin Last Six Hours
                </span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.hasTrialedOffClonidine}
                  onChange={e => updateInput('hasTrialedOffClonidine', e.target.checked)}
                  disabled={!inputs.hasEverTakenClonidine}
                  className="rounded"
                />
                <span className={!inputs.hasEverTakenClonidine ? 'text-gray-400' : ''}>Has Trialed Off Clonidine</span>
              </label>
            </div>
          </div>

          {/* In Utero Opiates */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">In Utero Opiates</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.inuteroOpiates.isTakingBuprenorphine}
                  onChange={e => updateInuteroOpiates('isTakingBuprenorphine', e.target.checked)}
                  className="rounded"
                />
                <span>Buprenorphine</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.inuteroOpiates.isTakingMethadone}
                  onChange={e => updateInuteroOpiates('isTakingMethadone', e.target.checked)}
                  className="rounded"
                />
                <span>Methadone</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={inputs.inuteroOpiates.isTakingOther}
                  onChange={e => updateInuteroOpiates('isTakingOther', e.target.checked)}
                  className="rounded"
                />
                <span>Other</span>
              </label>
            </div>
          </div>

          {/* Time-based inputs */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Time Parameters</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Hours Since Birth</label>
                <input
                  type="number"
                  value={inputs.hoursSinceBirth}
                  onChange={e => updateInput('hoursSinceBirth', parseInt(e.target.value) || 0)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${!inputs.hasEverTakenClonidine ? 'text-gray-400' : ''}`}
                >
                  Hours Since Last Clonidine Change
                </label>
                <input
                  type="number"
                  value={inputs.hoursSinceLastClonidineChange}
                  onChange={e => updateInput('hoursSinceLastClonidineChange', parseInt(e.target.value) || 0)}
                  disabled={!inputs.hasEverTakenClonidine}
                  className={`w-full p-2 border rounded ${
                    !inputs.hasEverTakenClonidine ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${!inputs.hasEverTakenMorphine ? 'text-gray-400' : ''}`}
                >
                  Hours Since Last Morphine Change
                </label>
                <input
                  type="number"
                  value={inputs.hoursSinceLastMorphineChange}
                  onChange={e => updateInput('hoursSinceLastMorphineChange', parseInt(e.target.value) || 0)}
                  disabled={!inputs.hasEverTakenMorphine}
                  className={`w-full p-2 border rounded ${
                    !inputs.hasEverTakenMorphine ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Run Button */}
          <button
            onClick={handleRunAlgorithm}
            disabled={
              (inputs.hasEverTakenMorphine && inputs.hoursSinceLastMorphineChange === 0) ||
              (inputs.hasEverTakenClonidine && inputs.hoursSinceLastClonidineChange === 0)
            }
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
          >
            Run Algorithm
          </button>
        </div>
      </div>

      {/* Right Side - Results */}
      <div className="w-1/2 p-6 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Algorithm Results</h2>

          {results ? (
            <div className="space-y-6">
              {/* Input Summary */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-green-800">Input Summary</h3>
                <div className="text-sm space-y-3">
                  {/* Symptoms */}
                  <div>
                    <h4 className="font-semibold text-gray-600 mb-1">Symptoms</h4>
                    <div className="ml-4 space-y-1">
                      <p>
                        <strong>Prior Symptoms:</strong>{' '}
                        {inputs.priorAssessmentActiveSymptoms.map(s => getSymptomName(s)).join(', ') || 'None'}
                      </p>
                      <p>
                        <strong>Current Symptoms:</strong>{' '}
                        {inputs.currentAssessmentActiveSymptoms.map(s => getSymptomName(s)).join(', ') || 'None'}
                      </p>
                    </div>
                  </div>

                  {/* Morphine */}
                  <div>
                    <h4 className="font-semibold text-gray-600 mb-1">Morphine</h4>
                    <div className="ml-4 space-y-1">
                      <p>
                        <strong>Prior Recommendation:</strong>{' '}
                        {getMorphineRecommendationDisplayName(inputs.priorMorphineRecommendation)}
                      </p>
                      <p className={inputs.currentMorphineDose > 0 ? 'line-through text-gray-400' : ''}>
                        <strong>Has Ever Taken:</strong> {inputs.hasEverTakenMorphine ? 'Yes' : 'No'}
                      </p>
                      <p>
                        <strong>Current Dose:</strong> {inputs.currentMorphineDose} mg/kg
                      </p>
                      <p className={!inputs.hasEverTakenMorphine ? 'line-through text-gray-400' : ''}>
                        <strong>Hours Since Last Change:</strong> {inputs.hoursSinceLastMorphineChange}
                      </p>
                    </div>
                  </div>

                  {/* Clonidine */}
                  <div>
                    <h4 className="font-semibold text-gray-600 mb-1">Clonidine</h4>
                    <div className="ml-4 space-y-1">
                      <p>
                        <strong>Last Assessment Recommendation:</strong>{' '}
                        {getClonidineRecommendationDisplayName(inputs.lastAssessmentClonidineRecommendation)}
                      </p>
                      <p className={inputs.clonidineDose > 0 ? 'line-through text-gray-400' : ''}>
                        <strong>Has Ever Taken:</strong> {inputs.hasEverTakenClonidine ? 'Yes' : 'No'}
                      </p>
                      <p>
                        <strong>Current Dose:</strong> {inputs.clonidineDose} mcg/kg q{inputs.clonidineFrequency}h
                      </p>
                      <p className={!inputs.hasEverTakenClonidine ? 'line-through text-gray-400' : ''}>
                        <strong>Increased Within Last Six Hours:</strong>{' '}
                        {inputs.isIncreaseComparedToSixHoursAgo ? 'Yes' : 'No'}
                      </p>
                      <p className={!inputs.hasEverTakenClonidine ? 'line-through text-gray-400' : ''}>
                        <strong>Has Trialed Off:</strong> {inputs.hasTrialedOffClonidine ? 'Yes' : 'No'}
                      </p>
                      <p className={!inputs.hasEverTakenClonidine ? 'line-through text-gray-400' : ''}>
                        <strong>Hours Since Last Change:</strong> {inputs.hoursSinceLastClonidineChange}
                      </p>
                    </div>
                  </div>

                  {/* Patient History */}
                  <div>
                    <h4 className="font-semibold text-gray-600 mb-1">Patient History</h4>
                    <div className="ml-4 space-y-1">
                      <p>
                        <strong>In Utero Opiates:</strong>{' '}
                        {Object.entries(inputs.inuteroOpiates)
                          .filter(([key, value]) => value)
                          .map(([key, value]) => key.replace('isTaking', ''))
                          .join(', ') || 'None'}
                      </p>
                      <p>
                        <strong>Hours Since Birth:</strong> {inputs.hoursSinceBirth}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assessment Scores */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">Assessment Scores</h3>
                <div className="text-sm space-y-3">
                  <p>
                    <strong>Prior Assessment Score:</strong> {results.priorAssessmentScore}
                  </p>
                  <p>
                    <strong>Current Assessment Score:</strong> {results.currentAssessmentScore}
                  </p>
                </div>
              </div>

              {/* Morphine Recommendation */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">Morphine Recommendation</h3>
                <div className="space-y-2">
                  <p>
                    <strong>Recommendation:</strong> {results.morphineRecommendation.recommendation}
                  </p>
                  <p>
                    <strong>Calculated Dose:</strong> {results.morphineRecommendation.calculatedDose} mg/kg
                  </p>
                  <p>
                    <strong>Force Clonidine Start:</strong>{' '}
                    {results.morphineRecommendation.forceClonidineStart ? 'Yes' : 'No'}
                  </p>
                  <p>
                    <strong>Note:</strong> {results.morphineRecommendation.note}
                  </p>
                </div>
              </div>

              {/* Clonidine Recommendation */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">Clonidine Recommendation</h3>
                <div className="space-y-2">
                  <p>
                    <strong>Recommendation:</strong> {results.clonidineRecommendation.recommendation}
                  </p>
                  <p>
                    <strong>Calculated Dose:</strong> {results.clonidineRecommendation.calculatedDose} mcg/kg q
                    {results.clonidineRecommendation.frequency}
                  </p>
                  <p>
                    <strong>Is Restart:</strong> {results.clonidineRecommendation.isRestart ? 'Yes' : 'No'}
                  </p>
                  <p>
                    <strong>Note:</strong> {results.clonidineRecommendation.note}
                  </p>
                </div>
              </div>

              {/* Comfort Measures */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">Comfort Measures</h3>
                <div className="space-y-2">
                  {results.comfortMeasures && results.comfortMeasures.length > 0 ? (
                    <ul className="list-disc pl-6">
                      {results.comfortMeasures.map((measure: any) => (
                        <li key={measure} className="bg-white rounded p-2 mb-1">
                          {measure}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No comfort measures recommended</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <p>Click "Run Algorithm" to see results</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
